import { useState } from 'react';
import { Badge, Button } from '@/components/atoms';
import {
  useConsumidores,
  useConsumidorUso,
  useCreateConsumidor,
  useRevokeConsumidor,
  useUpdateConsumidor,
} from '@/hooks/useConsumidores';
import { ApiError } from '@/services/api';
import { ESCOPOS, type Consumidor, type ConsumidorCreate, type Escopo } from '@/types/consumidores';
import styles from './ConfigConsumidores.module.css';

function curl(prefixo: string): string {
  const base = typeof window === 'undefined' ? '' : window.location.origin;
  return `curl -H "Authorization: Bearer ${prefixo}_<segredo>" ${base}/api/v1/dados/eu`;
}

const VAZIO: ConsumidorCreate = { nome: '', escopos: [], limite_por_minuto: 120 };

function UsoDoConsumidor({ id }: { id: string }) {
  const { data: uso, isLoading } = useConsumidorUso(id);
  if (isLoading || !uso) {
    return (
      <>
        <dt>Custo SERP do tenant no mês</dt><dd>—</dd>
      </>
    );
  }
  return (
    <>
      <dt>Custo SERP do tenant no mês</dt>
      <dd>
        US$ {uso.custo_serp_usd?.toFixed(2) ?? '—'}
        <div className={styles.muted}>{uso.custo_observacao}</div>
      </dd>
    </>
  );
}

export function ConfigConsumidores() {
  const { data: lista, isLoading } = useConsumidores();
  const criar = useCreateConsumidor();
  const editar = useUpdateConsumidor();
  const revogar = useRevokeConsumidor();
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState<Consumidor | null>(null);
  const [form, setForm] = useState<ConsumidorCreate>(VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [tokenNovo, setTokenNovo] = useState<{ token: string; prefixo: string } | null>(null);

  const abrirNovo = () => { setEditando(null); setForm(VAZIO); setErro(null); setAberto(true); };
  const abrirEdicao = (c: Consumidor) => {
    setEditando(c);
    setForm({ nome: c.nome, escopos: c.escopos, limite_por_minuto: c.limite_por_minuto });
    setErro(null); setAberto(true);
  };
  const toggleEscopo = (e: Escopo) =>
    setForm((f) => ({ ...f, escopos: f.escopos.includes(e) ? f.escopos.filter((x) => x !== e) : [...f.escopos, e] }));

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Dê um nome ao consumidor.'); return; }
    const payload = { ...form };
    try {
      if (editando) {
        await editar.mutateAsync({ id: editando.id, payload });
      } else {
        const criado = await criar.mutateAsync(payload);
        setTokenNovo({ token: criado.token, prefixo: criado.prefixo });
      }
      setAberto(false);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    }
  };

  const confirmarRevogar = async (c: Consumidor) => {
    if (!window.confirm(`Revogar "${c.nome}"? O token deixa de funcionar na hora e não volta.`)) return;
    await revogar.mutateAsync(c.id);
  };

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Consumidores da API de dados</h1>
          <p className={styles.muted}>Quem pode chamar <span className={styles.mono}>/api/v1/dados</span>: token de servidor, com escopos e limite por minuto. O token resolve: não há restrição de IP.</p>
        </div>
        <Button onClick={abrirNovo}>Novo consumidor</Button>
      </div>

      {tokenNovo && (
        <div className={styles.tokenBox} role="status">
          <strong>Token criado. Copie agora: ele não aparece de novo.</strong>
          <code className={styles.token}>{tokenNovo.token}</code>
          <div className={styles.actions}>
            <Button onClick={() => navigator.clipboard?.writeText(tokenNovo.token)}>Copiar</Button>
            <Button onClick={() => setTokenNovo(null)}>Já guardei</Button>
          </div>
          <pre className={styles.curl}>{curl(tokenNovo.prefixo)}</pre>
        </div>
      )}

      {isLoading && <p className={styles.muted}>Carregando…</p>}
      {!isLoading && (lista ?? []).length === 0 && <p className={styles.muted}>Nenhum consumidor ainda.</p>}

      <ul className={styles.list}>
        {(lista ?? []).map((c) => (
          <li key={c.id} className={c.revogado_em ? styles.cardRevogado : styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardTitle}>{c.nome}</span>
              {c.revogado_em ? <Badge>revogado</Badge> : <Badge>ativo</Badge>}
            </div>
            <dl className={styles.grid}>
              <dt>Prefixo</dt><dd className={styles.mono}>{c.prefixo}</dd>
              <dt>Escopos</dt><dd>{c.escopos.join(', ') || '—'}</dd>
              <dt>Limite</dt><dd>{c.limite_por_minuto}/min</dd>
              <dt>Chamadas no mês</dt><dd>{c.chamadas_mes}</dd>
              <dt>Último uso</dt><dd>{c.ultimo_uso_em ? `${new Date(c.ultimo_uso_em).toLocaleString('pt-BR')} · ${c.ultimo_ip ?? ''}` : '—'}</dd>
              {!c.revogado_em && <UsoDoConsumidor id={c.id} />}
            </dl>
            {!c.revogado_em && (
              <div className={styles.actions}>
                <Button onClick={() => abrirEdicao(c)}>Editar</Button>
                <Button onClick={() => confirmarRevogar(c)}>Revogar</Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {aberto && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} role="dialog" aria-label={editando ? 'Editar consumidor' : 'Novo consumidor'}>
            <h2 className={styles.modalTitle}>{editando ? 'Editar consumidor' : 'Novo consumidor'}</h2>
            <label className={styles.field}>
              <span className={styles.label}>Nome</span>
              <input className={styles.input} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </label>
            <fieldset className={styles.fieldset}>
              <legend className={styles.label}>Escopos</legend>
              {ESCOPOS.map((e) => (
                <label key={e.value} className={styles.check}>
                  <input type="checkbox" checked={form.escopos.includes(e.value)} onChange={() => toggleEscopo(e.value)} />
                  <span>{e.label}</span>
                </label>
              ))}
            </fieldset>
            <label className={styles.field}>
              <span className={styles.label}>Limite por minuto</span>
              <input className={styles.input} type="number" min={1} max={6000} value={form.limite_por_minuto}
                     onChange={(e) => setForm({ ...form, limite_por_minuto: Number(e.target.value) || 1 })} />
            </label>
            {erro && <p className={styles.crit}>{erro}</p>}
            <div className={styles.modalActions}>
              <Button onClick={() => setAberto(false)}>Cancelar</Button>
              <Button onClick={salvar}>{editando ? 'Salvar' : 'Criar'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
